import React, { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { api, type LodgingNight, type LodgingQuote, type LodgingStay, type MarkLodging } from '../../api';
import { RADIUS, TYPE, useTheme } from '../../theme';
import { Field, Icon, IconButton, Input, Switch } from '../../components/ui';
import { kronor } from './adPurchase';
import { addDays, extraText, guestsText, nightsText, shortDay, todayIso } from './lodging';

/**
 * Välj nätter, antal personer och tillval för ett boende (L1). Visar lediga bäddar per natt och
 * priset från API:t (lodgingQuote). Används både för fristående bokning och i annonsens köp.
 */
export function LodgingStayPicker({ lodging, value, onChange, onQuote }: {
  lodging: MarkLodging; value: LodgingStay; onChange: (v: LodgingStay) => void;
  /** Senaste priset (null medan det räknas eller om valet är ogiltigt). */
  onQuote?: (q: LodgingQuote | null) => void;
}) {
  const T = useTheme();
  const [nights, setNights] = useState<LodgingNight[]>([]);
  const [quote, setQuote] = useState<LodgingQuote | null>(null);
  const [err, setErr] = useState('');
  const today = todayIso();
  const set = (patch: Partial<LodgingStay>) => onChange({ ...value, ...patch });

  // Lediga nätter: från två dagar före ankomst till två dagar efter avresa (högst två veckor).
  useEffect(() => {
    if (!value.checkIn || !value.checkOut || value.checkOut <= value.checkIn) { setNights([]); return; }
    let off = false;
    const from = addDays(value.checkIn, -2) < today ? today : addDays(value.checkIn, -2);
    const to = [addDays(value.checkOut, 2), addDays(from, 14)].sort()[0];
    api.lodgingAvailability(lodging.id, from, to).then(n => { if (!off) setNights(n); }).catch(() => { if (!off) setNights([]); });
    return () => { off = true; };
  }, [lodging.id, value.checkIn, value.checkOut, today]);

  useEffect(() => {
    let off = false;
    setQuote(null); onQuote?.(null); setErr('');
    if (!value.checkIn || !value.checkOut) return;
    const t = setTimeout(() => {
      api.lodgingQuote(value)
        .then(q => { if (!off) { setQuote(q); onQuote?.(q); } })
        .catch((e: Error) => { if (!off) setErr(e.message); });
    }, 250);
    return () => { off = true; clearTimeout(t); };
  }, [value.lodgingId, value.checkIn, value.checkOut, value.guests, value.linens, value.cleaning]); // eslint-disable-line react-hooks/exhaustive-deps

  const linens = extraText(lodging.linensIncluded, lodging.linensPrice, lodging.linensUnit);
  const cleaning = extraText(lodging.cleaningIncluded, lodging.cleaningPrice, lodging.cleaningUnit);
  const inStay = (d: string) => d >= value.checkIn && d < value.checkOut;
  const need = lodging.priceUnit === 'lodging' ? lodging.capacity : value.guests;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="lodging-picker">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        <Field label="Ankomst">
          <Input type="date" value={value.checkIn} min={today} data-testid="lodging-checkin"
            onChange={e => { const d = e.target.value; set({ checkIn: d, checkOut: value.checkOut > d ? value.checkOut : addDays(d, 1) }); }} />
        </Field>
        <Field label="Avresa">
          <Input type="date" value={value.checkOut} min={value.checkIn ? addDays(value.checkIn, 1) : today} data-testid="lodging-checkout"
            onChange={e => set({ checkOut: e.target.value })} />
        </Field>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Antal personer</div>
          <div style={{ fontSize: 13, color: T.textMuted }}>Högst {lodging.capacity}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <IconButton label="Färre personer" disabled={value.guests <= 1} onClick={() => set({ guests: value.guests - 1 })}><Icon as={Minus} size={18} /></IconButton>
          <span aria-live="polite" data-testid="lodging-guests" style={{ minWidth: 28, textAlign: 'center', fontSize: 17, fontWeight: 800, color: T.text }}>{value.guests}</span>
          <IconButton label="Fler personer" disabled={value.guests >= lodging.capacity} onClick={() => set({ guests: value.guests + 1 })}><Icon as={Plus} size={18} /></IconButton>
        </div>
      </div>

      {(linens || cleaning) && (
        <div style={{ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${T.border}` }}>
          {linens && (lodging.linensIncluded
            ? <div style={{ display: 'flex', justifyContent: 'space-between', minHeight: 44, alignItems: 'center', fontSize: 14, color: T.text }}><span>Lakan och handdukar</span><b style={{ color: T.ok }}>Ingår</b></div>
            : <Switch checked={value.linens} onChange={v => set({ linens: v })} label={`Lakan och handdukar · ${linens}`} style={{ justifyContent: 'space-between', width: '100%', color: T.text }} />)}
          {cleaning && (lodging.cleaningIncluded
            ? <div style={{ display: 'flex', justifyContent: 'space-between', minHeight: 44, alignItems: 'center', fontSize: 14, color: T.text }}><span>Städning</span><b style={{ color: T.ok }}>Ingår</b></div>
            : <Switch checked={value.cleaning} onChange={v => set({ cleaning: v })} label={`Städning · ${cleaning}`} style={{ justifyContent: 'space-between', width: '100%', color: T.text }} />)}
        </div>
      )}

      {nights.length > 0 && (
        <div>
          <div style={{ ...TYPE.label, color: T.textMuted, marginBottom: 6 }}>Lediga bäddar per natt</div>
          <div className="hide-scrollbar" style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 2 }} data-testid="lodging-nights">
            {nights.map(n => {
              const full = n.bedsFree < need;
              const closed = !n.bookable;
              const sel = inStay(n.date);
              const tone = closed || full ? T.danger : T.ok;
              return (
                <button key={n.date} type="button" title={closed ? 'Marken tar inte emot bokningar' : `${n.bedsFree} av ${lodging.capacity} bäddar lediga`}
                  onClick={() => set({ checkIn: n.date, checkOut: value.checkOut > n.date ? value.checkOut : addDays(n.date, 1) })}
                  style={{
                    flex: '0 0 auto', minWidth: 64, minHeight: 52, padding: '6px 8px', borderRadius: RADIUS.sm, cursor: 'pointer', fontFamily: 'inherit',
                    border: `${sel ? 2 : 1}px solid ${sel ? T.accentFill : T.border}`, background: sel ? T.accentSoft : T.surface, color: T.text, textAlign: 'center',
                  }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{shortDay(n.date)}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: tone }}>{closed ? 'Stängt' : n.bedsFree === 0 ? 'Fullt' : lodging.priceUnit === 'lodging' ? (full ? 'Upptaget' : 'Ledigt') : `${n.bedsFree} lediga`}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {quote && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 10, borderTop: `1px solid ${T.border}` }} data-testid="lodging-quote">
          {quote.lines.map(l => (
            <div key={l.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14, color: T.textSub }}>
              <span>{l.name}</span><span>{kronor(l.amount)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800, color: T.text }}>
            <span>Boende, {nightsText(quote.nights)}, {guestsText(quote.guests)}</span><span>{kronor(quote.amount)}</span>
          </div>
          {!quote.available && <div role="alert" data-testid="lodging-unavailable" style={{ fontSize: 13, fontWeight: 600, color: T.danger }}>{quote.unavailableReason}</div>}
        </div>
      )}
      {err && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: T.danger }}>{err}</div>}
    </div>
  );
}
